import { loadBaseCSS } from '../shared/button-utils.js';
import '../content-palette/qds-content-palette.js';
import '../content-canvas/qds-content-canvas.js';
import '../content-properties/qds-content-properties.js';
import '../content-renderer/qds-content-renderer.js';
import {
  cloneContentDocument,
  createBlockId,
  createBlockInstance,
  ensureDashboardBounds,
  findBlockById,
  escapeHtml,
  findBlockTreeLocation,
  findFirstBlock,
  getContentBlockDisplayName,
  insertBlockAtTarget,
  normalizeBlockInstance,
  normalizeContentDocument,
  removeBlockFromTree,
  resolveRegistry,
  resequencePageBlocks,
  sortBlocksForMode,
  updateBlockInTree
} from '../content-blocks/qds-content-blocks.js';
import type {
  QdsContentBlockInstance,
  QdsContentDocument,
  QdsContentMode
} from '../../types/content.js';
import type { QdsBlockRegistry } from '../../types/blocks.js';
import type {
  QdsBlockAddDetail,
  QdsBlockDropDetail,
  QdsBlockMoveDetail,
  QdsBlockRemoveDetail,
  QdsBlockSelectDetail,
  QdsContentChangeDetail,
  QdsDesignerContext,
  QdsPreviewRequestDetail
} from '../../types/designer.js';

interface QdsAddBlockRequestDetail {
  type: string;
  target?: QdsBlockDropDetail['target'];
}

loadBaseCSS();

type DesignerView = 'design' | 'preview';

/**
 * @element qds-content-designer
 *
 * Orchestrates the content authoring experience, composing the palette, canvas, properties panel, and preview renderer to edit a content document driven by a host-supplied block registry.
 *
 * @attr {boolean} readonly - When present, disables editing and switches child panels into a read-only state
 * @attr {string} selected-block-id - Id of the block currently selected in the designer
 *
 * @property {QdsContentDocument | null} document - The content document being edited
 * @property {QdsBlockRegistry | null} registry - The block registry used to resolve block definitions
 * @property {QdsDesignerContext | undefined} context - Host-supplied designer context (registry, capabilities)
 * @property {boolean} readonly - Whether editing is disabled
 * @property {string | null} selectedBlockId - Id of the currently selected block
 *
 * @fires content-change - Fired whenever the content document is mutated, with the updated document
 * @fires block-select - Fired when the selected block changes
 * @fires block-add - Fired when a new block is added to the document
 * @fires block-remove - Fired when a block is removed from the document
 * @fires block-move - Fired when a block is reordered or moved to a new target
 * @fires preview-request - Fired when the preview view is activated, with the current document
 */
export class QdsContentDesigner extends HTMLElement {
  private _document: QdsContentDocument = normalizeContentDocument(null);
  private _registry: QdsBlockRegistry | null = null;
  private _context?: QdsDesignerContext;
  private _readonly = false;
  private _selectedBlockId: string | null = null;
  private _view: DesignerView = 'design';

  static get observedAttributes(): string[] {
    return ['readonly', 'selected-block-id'];
  }

  constructor() {
    super();
    this.attachShadow({ mode: 'open' });
  }

  connectedCallback(): void {
    if (!this.isConnected || !this.shadowRoot) {
      return;
    }

    this.shadowRoot.addEventListener('click', this._handleClick);
    this.shadowRoot.addEventListener('add-block', this._handleCustomEvent as EventListener);
    this.shadowRoot.addEventListener('block-select', this._handleCustomEvent as EventListener);
    this.shadowRoot.addEventListener('block-duplicate', this._handleCustomEvent as EventListener);
    this.shadowRoot.addEventListener('block-remove', this._handleCustomEvent as EventListener);
    this.shadowRoot.addEventListener('block-reorder', this._handleCustomEvent as EventListener);
    this.shadowRoot.addEventListener('block-drop', this._handleCustomEvent as EventListener);
    this.shadowRoot.addEventListener('block-visibility-toggle', this._handleCustomEvent as EventListener);
    this.shadowRoot.addEventListener('block-layout-change', this._handleCustomEvent as EventListener);
    this.shadowRoot.addEventListener('document-change', this._handleCustomEvent as EventListener);
    this.shadowRoot.addEventListener('document-layout-change', this._handleCustomEvent as EventListener);
    this.shadowRoot.addEventListener('document-canvas-change', this._handleCustomEvent as EventListener);
    this.shadowRoot.addEventListener('block-meta-change', this._handleCustomEvent as EventListener);
    this.shadowRoot.addEventListener('block-config-change', this._handleCustomEvent as EventListener);

    this.render();
  }

  disconnectedCallback(): void {
    this.shadowRoot?.removeEventListener('click', this._handleClick);
    this.shadowRoot?.removeEventListener('add-block', this._handleCustomEvent as EventListener);
    this.shadowRoot?.removeEventListener('block-select', this._handleCustomEvent as EventListener);
    this.shadowRoot?.removeEventListener('block-duplicate', this._handleCustomEvent as EventListener);
    this.shadowRoot?.removeEventListener('block-remove', this._handleCustomEvent as EventListener);
    this.shadowRoot?.removeEventListener('block-reorder', this._handleCustomEvent as EventListener);
    this.shadowRoot?.removeEventListener('block-drop', this._handleCustomEvent as EventListener);
    this.shadowRoot?.removeEventListener('block-visibility-toggle', this._handleCustomEvent as EventListener);
    this.shadowRoot?.removeEventListener('block-layout-change', this._handleCustomEvent as EventListener);
    this.shadowRoot?.removeEventListener('document-change', this._handleCustomEvent as EventListener);
    this.shadowRoot?.removeEventListener('document-layout-change', this._handleCustomEvent as EventListener);
    this.shadowRoot?.removeEventListener('document-canvas-change', this._handleCustomEvent as EventListener);
    this.shadowRoot?.removeEventListener('block-meta-change', this._handleCustomEvent as EventListener);
    this.shadowRoot?.removeEventListener('block-config-change', this._handleCustomEvent as EventListener);
  }

  attributeChangedCallback(name: string, oldValue: string | null, newValue: string | null): void {
    if (oldValue === newValue) {
      return;
    }

    if (name === 'readonly') {
      this._readonly = newValue !== null;
    }

    if (name === 'selected-block-id') {
      this._selectedBlockId = newValue;
    }

    this.render();
  }

  get document(): QdsContentDocument | null {
    return this._document;
  }

  set document(value: QdsContentDocument | null) {
    this._document = normalizeContentDocument(value, this._registry || this._context?.blockRegistry || null);
    if (this._selectedBlockId && !findBlockById(this._document.blocks, this._selectedBlockId, 'page')) {
      this._selectedBlockId = findFirstBlock(this._document.blocks, this._document.mode)?.id || null;
    }
    this.render();
  }

  get registry(): QdsBlockRegistry | null {
    return this._registry;
  }

  set registry(value: QdsBlockRegistry | null) {
    this._registry = value;
    this._document = normalizeContentDocument(this._document, this._registry || this._context?.blockRegistry || null);
    this.render();
  }

  get context(): QdsDesignerContext | undefined {
    return this._context;
  }

  set context(value: QdsDesignerContext | undefined) {
    this._context = value;
    this.render();
  }

  get readonly(): boolean {
    return this._readonly;
  }

  set readonly(value: boolean) {
    this._readonly = Boolean(value);
    if (this._readonly) {
      this.setAttribute('readonly', '');
    } else {
      this.removeAttribute('readonly');
    }
  }

  get selectedBlockId(): string | null {
    return this._selectedBlockId;
  }

  set selectedBlockId(value: string | null) {
    this._selectedBlockId = value;
    if (value) {
      this.setAttribute('selected-block-id', value);
    } else {
      this.removeAttribute('selected-block-id');
    }
  }

  private _canEdit(): boolean {
    return !this._readonly && this._context?.canEdit !== false;
  }

  private _canPreview(): boolean {
    return this._context?.canPreview !== false;
  }

  private _getRegistry(): QdsBlockRegistry {
    return resolveRegistry(this._registry || this._context?.blockRegistry || null);
  }

  private _emit(name: string, detail: Record<string, unknown>): void {
    this.dispatchEvent(new CustomEvent(name, {
      bubbles: true,
      composed: true,
      detail
    }));
  }

  private _emitContentChange(): void {
    this._emit('content-change', {
      document: cloneContentDocument(this._document)
    } satisfies QdsContentChangeDetail);
  }

  private _setSelection(blockId: string | null): void {
    this._selectedBlockId = blockId;
    this._emit('block-select', {
      blockId
    } satisfies QdsBlockSelectDetail);
    this.render();
  }

  private _commitDocument(
    mutator: (documentModel: QdsContentDocument) => void,
    options: {
      selectBlockId?: string | null;
      extraEvent?: { name: string; detail: Record<string, unknown> };
    } = {}
  ): void {
    const registry = this._getRegistry();
    const nextDocument = cloneContentDocument(this._document);
    mutator(nextDocument);
    nextDocument.blocks = nextDocument.mode === 'page'
      ? resequencePageBlocks(nextDocument.blocks)
      : nextDocument.blocks;
    this._document = normalizeContentDocument(nextDocument, registry);

    if (options.selectBlockId !== undefined) {
      this._selectedBlockId = options.selectBlockId;
    } else if (this._selectedBlockId && !findBlockById(this._document.blocks, this._selectedBlockId, 'page')) {
      this._selectedBlockId = findFirstBlock(this._document.blocks, this._document.mode)?.id || null;
    }

    this.render();
    this._emitContentChange();

    if (options.extraEvent) {
      this._emit(options.extraEvent.name, options.extraEvent.detail);
    }
  }

  private _movePageBlock(blockId: string, direction: 'up' | 'down'): void {
    const location = findBlockTreeLocation(this._document.blocks, blockId, 'page');
    if (!location) {
      return;
    }

    const orderedSiblings = sortBlocksForMode(location.siblings, 'page');
    const orderedIndex = orderedSiblings.findIndex((block) => block.id === blockId);
    const nextIndex = direction === 'up' ? orderedIndex - 1 : orderedIndex + 1;
    if (orderedIndex === -1 || nextIndex < 0 || nextIndex >= orderedSiblings.length) {
      return;
    }

    const reordered = [...orderedSiblings];
    const [block] = reordered.splice(orderedIndex, 1);
    reordered.splice(nextIndex, 0, block);
    const orderedWithLayout = reordered.map((entry, index) => ({
      ...entry,
      layout: {
        ...(entry.layout || {}),
        order: index
      }
    }));

    this._commitDocument((documentModel) => {
      const targetSiblings = location.parentBlockId
        ? findBlockById(documentModel.blocks, location.parentBlockId, 'page')?.children || []
        : documentModel.blocks;
      targetSiblings.splice(0, targetSiblings.length, ...orderedWithLayout);
    }, {
      selectBlockId: blockId,
      extraEvent: {
        name: 'block-move',
        detail: {
          blockId,
          layout: {
            ...(block.layout || {}),
            order: nextIndex
          }
        } satisfies QdsBlockMoveDetail
      }
    });
  }

  private _movePageBlockToTarget(blockId: string, target: QdsBlockDropDetail['target']): void {
    const location = findBlockTreeLocation(this._document.blocks, blockId, 'page');
    if (!location) {
      return;
    }

    let currentParentId = target.parentBlockId;
    while (currentParentId) {
      if (currentParentId === blockId) {
        return;
      }

      currentParentId = findBlockTreeLocation(this._document.blocks, currentParentId, 'page')?.parentBlockId || null;
    }

    const isSameParent = location.parentBlockId === target.parentBlockId;
    const boundedTargetIndex = Math.max(0, target.index);
    const insertIndex = isSameParent && boundedTargetIndex > location.index
      ? boundedTargetIndex - 1
      : boundedTargetIndex;
    if (isSameParent && insertIndex === location.index) {
      return;
    }

    this._commitDocument((documentModel) => {
      const removed = removeBlockFromTree(documentModel.blocks, blockId, 'page');
      if (!removed) {
        return;
      }

      insertBlockAtTarget(documentModel.blocks, removed.removedBlock, {
        parentBlockId: target.parentBlockId,
        index: insertIndex
      }, 'page');

      const targetSiblings = target.parentBlockId
        ? findBlockById(documentModel.blocks, target.parentBlockId, 'page')?.children || []
        : documentModel.blocks;
      targetSiblings.splice(0, targetSiblings.length, ...targetSiblings.map((entry, index) => ({
        ...entry,
        layout: {
          ...(entry.layout || {}),
          order: index
        }
      })));
    }, {
      selectBlockId: blockId,
      extraEvent: {
        name: 'block-move',
        detail: {
          blockId,
          layout: {
            ...(location.block.layout || {}),
            order: insertIndex
          }
        } satisfies QdsBlockMoveDetail
      }
    });
  }

  private _duplicateBlock(blockId: string): void {
    const registry = this._getRegistry();
    const location = findBlockTreeLocation(this._document.blocks, blockId, 'page');
    if (!location) {
      return;
    }

    const originalBlock = location.block;
    const clone: QdsContentBlockInstance = {
      ...cloneContentDocument(originalBlock),
      id: createBlockId(originalBlock.type),
      title: `${getContentBlockDisplayName(originalBlock, registry)} Copy`
    };

    if (this._document.mode === 'dashboard') {
      const columns = this._document.layout?.columns || 12;
      clone.layout = ensureDashboardBounds({
        ...(clone.layout || {}),
        x: (clone.layout?.x || 0) + 1,
        y: (clone.layout?.y || 0) + 1
      }, columns);
    }

    this._commitDocument((documentModel) => {
      insertBlockAtTarget(documentModel.blocks, normalizeBlockInstance(
        clone,
        location.index + 1,
        documentModel.mode,
        registry,
        documentModel.layout?.columns || 12
      ), {
        parentBlockId: location.parentBlockId,
        index: location.index + 1
      }, 'page');
    }, {
      selectBlockId: clone.id
    });
  }

  private _handleAddBlock(detail: QdsAddBlockRequestDetail): void {
    if (!this._canEdit()) {
      return;
    }

    const registry = this._getRegistry();
    const definition = registry.get(detail.type);
    if (!definition) {
      return;
    }

    const block = createBlockInstance(
      definition,
      this._document.mode,
      Math.max(0, detail.target?.index ?? this._document.blocks.length),
      this._document.layout?.columns || 12
    );

    this._commitDocument((documentModel) => {
      insertBlockAtTarget(documentModel.blocks, block, {
        parentBlockId: detail.target?.parentBlockId || null,
        index: Math.max(0, detail.target?.index ?? documentModel.blocks.length)
      }, 'page');
    }, {
      selectBlockId: block.id,
      extraEvent: {
        name: 'block-add',
        detail: {
          block
        } satisfies QdsBlockAddDetail
      }
    });
  }

  private _handleModeChange(mode: QdsContentMode): void {
    if (!this._canEdit()) {
      return;
    }

    const registry = this._getRegistry();
    this._commitDocument((documentModel) => {
      documentModel.mode = mode;
      documentModel.blocks = documentModel.blocks.map((block, index) => normalizeBlockInstance(
        block,
        index,
        mode,
        registry,
        documentModel.layout?.columns || 12
      ));
      if (mode === 'page') {
        documentModel.blocks = resequencePageBlocks(documentModel.blocks);
      }
    });
  }

  private _handleCustomEvent = (event: CustomEvent): void => {
    if (event.target === this) {
      return;
    }

    switch (event.type) {
      case 'add-block':
        this._handleAddBlock(event.detail);
        break;
      case 'block-select':
        this._setSelection(event.detail.blockId || null);
        break;
      case 'block-duplicate':
        if (this._canEdit()) {
          this._duplicateBlock(event.detail.blockId);
        }
        break;
      case 'block-remove':
        if (this._canEdit()) {
          this._commitDocument((documentModel) => {
            removeBlockFromTree(documentModel.blocks, event.detail.blockId, 'page');
          }, {
            selectBlockId: this._selectedBlockId === event.detail.blockId ? null : this._selectedBlockId,
            extraEvent: {
              name: 'block-remove',
              detail: {
                blockId: event.detail.blockId
              } satisfies QdsBlockRemoveDetail
            }
          });
        }
        break;
      case 'block-reorder':
        if (this._canEdit()) {
          this._movePageBlock(event.detail.blockId, event.detail.direction);
        }
        break;
      case 'block-drop':
        if (this._canEdit()) {
          const detail = event.detail as QdsBlockDropDetail;
          if (detail.item.kind === 'new') {
            this._handleAddBlock({
              type: detail.item.type,
              target: detail.target
            });
          } else {
            this._movePageBlockToTarget(detail.item.blockId, detail.target);
          }
        }
        break;
      case 'block-visibility-toggle':
        if (this._canEdit()) {
          this._commitDocument((documentModel) => {
            updateBlockInTree(documentModel.blocks, event.detail.blockId, (block) => ({
              ...block,
              visibility: {
                ...(block.visibility || {}),
                hidden: !block.visibility?.hidden
              }
            }), 'page');
          }, {
            selectBlockId: event.detail.blockId
          });
        }
        break;
      case 'block-layout-change':
        if (this._canEdit()) {
          this._commitDocument((documentModel) => {
            updateBlockInTree(documentModel.blocks, event.detail.blockId, (block) => {
              if (documentModel.mode === 'dashboard') {
                return {
                  ...block,
                  layout: ensureDashboardBounds({
                    ...(block.layout || {}),
                    ...(event.detail.layout || {})
                  }, documentModel.layout?.columns || 12)
                };
              }

              return {
                ...block,
                layout: {
                  ...(block.layout || {}),
                  ...(event.detail.layout || {})
                }
              };
            }, 'page');
          }, {
            selectBlockId: event.detail.blockId,
            extraEvent: {
              name: 'block-move',
              detail: {
                blockId: event.detail.blockId,
                layout: event.detail.layout
              } satisfies QdsBlockMoveDetail
            }
          });
        }
        break;
      case 'document-change':
        if (this._canEdit()) {
          const nextMode = event.detail.document?.mode === 'dashboard' ? 'dashboard' : 'page';
          const modeChanged = nextMode !== this._document.mode;
          this._commitDocument((documentModel) => {
            documentModel.title = event.detail.document?.title || '';
            documentModel.summary = event.detail.document?.summary || '';
            documentModel.bodyHtml = event.detail.document?.bodyHtml || '';
            documentModel.mode = nextMode;
            if (modeChanged) {
              const registry = this._getRegistry();
              documentModel.blocks = documentModel.blocks.map((block, index) => normalizeBlockInstance(
                block,
                index,
                nextMode,
                registry,
                documentModel.layout?.columns || 12
              ));
            }
          });
        }
        break;
      case 'document-layout-change':
        if (this._canEdit()) {
          this._commitDocument((documentModel) => {
            documentModel.layout = {
              ...(documentModel.layout || {}),
              ...(event.detail.layout || {})
            };
          });
        }
        break;
      case 'document-canvas-change':
        if (this._canEdit()) {
          this._commitDocument((documentModel) => {
            documentModel.canvas = {
              ...(documentModel.canvas || {}),
              ...(event.detail.canvas || {})
            };
          });
        }
        break;
      case 'block-meta-change':
        if (this._canEdit()) {
          this._commitDocument((documentModel) => {
            updateBlockInTree(documentModel.blocks, event.detail.blockId, (block) => ({
              ...block,
              title: event.detail.title || '',
              visibility: {
                ...(block.visibility || {}),
                ...(event.detail.visibility || {})
              }
            }), 'page');
          }, {
            selectBlockId: event.detail.blockId
          });
        }
        break;
      case 'block-config-change':
        if (this._canEdit()) {
          this._commitDocument((documentModel) => {
            updateBlockInTree(documentModel.blocks, event.detail.blockId, (block) => ({
              ...block,
              config: {
                ...(event.detail.config || {})
              }
            }), 'page');
          }, {
            selectBlockId: event.detail.blockId
          });
        }
        break;
      default:
        break;
    }
  };

  private _handleClick = (event: Event): void => {
    const target = event.target as HTMLElement | null;
    const actionElement = target?.closest<HTMLElement>('[data-action]');

    if (!actionElement) {
      return;
    }

    const action = actionElement.dataset.action;
    if (!action) {
      return;
    }

    if (action === 'set-view') {
      const nextView = actionElement.dataset.view === 'preview' ? 'preview' : 'design';
      if (nextView === 'preview' && !this._canPreview()) {
        return;
      }

      this._view = nextView;
      this.render();

      if (nextView === 'preview') {
        this._emit('preview-request', {
          document: cloneContentDocument(this._document)
        } satisfies QdsPreviewRequestDetail);
      }
      return;
    }

    if (action === 'set-mode' && this._canEdit()) {
      const mode = actionElement.dataset.mode === 'dashboard' ? 'dashboard' : 'page';
      this._handleModeChange(mode);
    }
  };

  private _bindChildren(documentModel: QdsContentDocument, registry: QdsBlockRegistry): void {
    const palette = this.shadowRoot?.getElementById('designer-palette') as any;
    const canvas = this.shadowRoot?.getElementById('designer-canvas') as any;
    const preview = this.shadowRoot?.getElementById('designer-preview') as any;
    const properties = this.shadowRoot?.getElementById('designer-properties') as any;

    if (palette) {
      palette.mode = documentModel.mode;
      palette.registry = registry;
      palette.readonly = !this._canEdit();
    }

    if (canvas) {
      canvas.document = documentModel;
      canvas.registry = registry;
      canvas.context = this._context || { blockRegistry: registry };
      canvas.selectedBlockId = this._selectedBlockId;
      canvas.readonly = !this._canEdit();
    }

    if (preview) {
      preview.document = documentModel;
      preview.registry = registry;
      preview.context = this._context || { blockRegistry: registry };
      preview.mode = 'preview';
    }

    if (properties) {
      properties.document = documentModel;
      properties.registry = registry;
      properties.context = this._context || { blockRegistry: registry };
      properties.selectedBlockId = this._selectedBlockId;
      properties.readonly = !this._canEdit();
    }
  }

  render(): void {
    if (!this.shadowRoot) {
      return;
    }

    const registry = this._getRegistry();
    const documentModel = normalizeContentDocument(this._document, registry);
    const selectedBlock = this._selectedBlockId
      ? findBlockById(documentModel.blocks, this._selectedBlockId, 'page')
      : null;

    this.shadowRoot.innerHTML = `
      <style>
        :host {
          display: block;
          color: var(--theme-text-primary, #333333);
          font-family: var(--font-family-primary, 'IBM Plex Sans', sans-serif);
        }

        .designer {
          display: grid;
          gap: 20px;
        }

        .designer__header {
          display: flex;
          flex-wrap: wrap;
          align-items: flex-start;
          justify-content: space-between;
          gap: 16px;
          padding: 20px;
          border: 1px solid var(--theme-border-color, #e0e0e0);
          border-radius: 12px;
          background: var(--theme-background-primary, #ffffff);
          box-shadow: var(--shadow-light, 0 1px 3px rgba(32, 33, 36, 0.1));
        }

        .designer__headline {
          display: grid;
          gap: 8px;
        }

        .designer__title {
          margin: 0;
          font-size: clamp(1.4rem, 1vw + 1.1rem, 2rem);
          line-height: 1.1;
        }

        .designer__summary {
          margin: 0;
          color: var(--theme-text-secondary, #5f6368);
          max-width: 70ch;
        }

        .designer__status {
          display: flex;
          flex-wrap: wrap;
          gap: 8px;
        }

        .designer__badge {
          display: inline-flex;
          align-items: center;
          min-height: 28px;
          padding: 4px 10px;
          border-radius: 999px;
          background: var(--theme-background-secondary, #f8f8f8);
          color: var(--theme-text-secondary, #5f6368);
          font-size: 12px;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.06em;
        }

        .designer__toolbar {
          display: flex;
          flex-wrap: wrap;
          justify-content: flex-end;
          gap: 10px;
        }

        .designer__toolbar-group {
          display: flex;
          flex-wrap: wrap;
          gap: 8px;
        }

        .designer__button {
          min-height: 40px;
          padding: 10px 14px;
          border: 1px solid var(--theme-border-color, #e0e0e0);
          border-radius: 8px;
          background: var(--theme-background-secondary, #f8f8f8);
          color: inherit;
          cursor: pointer;
          transition: background-color 0.2s ease, border-color 0.2s ease;
        }

        .designer__button--active {
          border-color: var(--input-focus-border, #4285f4);
          background: rgba(66, 133, 244, 0.08);
        }

        .designer__button:focus-visible {
          outline: 2px solid var(--input-focus-border, #4285f4);
          outline-offset: 2px;
        }

        .designer__workspace {
          display: grid;
          grid-template-columns: minmax(260px, 300px) minmax(0, 1fr) minmax(280px, 340px);
          gap: 20px;
          align-items: start;
        }

        .designer__rail {
          display: grid;
          gap: 20px;
          align-content: start;
        }

        .designer__main {
          display: grid;
          gap: 20px;
          align-content: start;
        }

        .designer__panel {
          padding: 16px;
          border: 1px solid var(--theme-border-color, #e0e0e0);
          border-radius: 0;
          background: var(--theme-background-primary, #ffffff);
          box-shadow: var(--shadow-light, 0 1px 3px rgba(32, 33, 36, 0.1));
        }

        .designer__panel--flush {
          padding: 0;
        }

        .designer__panel--bare {
          border: 0;
          background: transparent;
          box-shadow: none;
        }

        .designer__selection {
          margin: 0;
          color: var(--theme-text-secondary, #5f6368);
          font-size: 13px;
        }

        @media (max-width: 1400px) {
          .designer__workspace {
            grid-template-columns: minmax(240px, 280px) minmax(0, 1fr);
          }

          .designer__rail--right {
            grid-column: 1 / -1;
          }
        }

        @media (max-width: 1200px) {
          .designer__workspace {
            grid-template-columns: minmax(0, 1fr);
          }

          .designer__rail {
            grid-template-columns: minmax(0, 1fr);
          }
        }
      </style>
      <section class="designer">
        <header class="designer__header">
          <div class="designer__headline">
            <div class="designer__status">
              <span class="designer__badge">${documentModel.mode}</span>
              <span class="designer__badge">${this._view}</span>
              ${selectedBlock ? `<span class="designer__badge">${escapeHtml(getContentBlockDisplayName(selectedBlock, registry))}</span>` : ''}
            </div>
            <h1 class="designer__title">${escapeHtml(documentModel.title || 'Untitled Content Document')}</h1>
            <p class="designer__summary">${escapeHtml(documentModel.summary || 'Compose reusable page and dashboard content with a host-supplied block registry.')}</p>
            <p class="designer__selection">${selectedBlock ? `Selected block: ${escapeHtml(getContentBlockDisplayName(selectedBlock, registry))}` : 'No block selected.'}</p>
          </div>
          <div class="designer__toolbar">
            <div class="designer__toolbar-group">
              <button
                type="button"
                class="designer__button ${documentModel.mode === 'page' ? 'designer__button--active' : ''}"
                data-action="set-mode"
                data-mode="page"
                ${!this._canEdit() ? 'disabled' : ''}
              >Page</button>
              <button
                type="button"
                class="designer__button ${documentModel.mode === 'dashboard' ? 'designer__button--active' : ''}"
                data-action="set-mode"
                data-mode="dashboard"
                ${!this._canEdit() ? 'disabled' : ''}
              >Dashboard</button>
            </div>
            <div class="designer__toolbar-group">
              <button
                type="button"
                class="designer__button ${this._view === 'design' ? 'designer__button--active' : ''}"
                data-action="set-view"
                data-view="design"
              >Design</button>
              <button
                type="button"
                class="designer__button ${this._view === 'preview' ? 'designer__button--active' : ''}"
                data-action="set-view"
                data-view="preview"
                ${!this._canPreview() ? 'disabled' : ''}
              >Preview</button>
            </div>
          </div>
        </header>
        <div class="designer__workspace">
          <aside class="designer__rail designer__rail--left">
            <div class="designer__panel">
              <qds-content-palette id="designer-palette"></qds-content-palette>
            </div>
          </aside>
          <main class="designer__main">
            <div class="designer__panel designer__panel--flush ${this._view === 'preview' ? 'designer__panel--bare' : ''}">
              ${this._view === 'preview'
                ? '<qds-content-renderer id="designer-preview" mode="preview"></qds-content-renderer>'
                : '<qds-content-canvas id="designer-canvas"></qds-content-canvas>'}
            </div>
          </main>
          <aside class="designer__rail designer__rail--right">
            <qds-content-properties id="designer-properties"></qds-content-properties>
          </aside>
        </div>
      </section>
    `;

    this._bindChildren(documentModel, registry);
  }
}

if (!customElements.get('qds-content-designer')) {
  customElements.define('qds-content-designer', QdsContentDesigner);
}

export default QdsContentDesigner;
